const GOOGLE_ANALYTICS_DESTINATION_ID = 'G-8MPJ6BD9RE';

const BASE_LINE_ITEM_CODES = [
  'line-item/item',
  'line-item/hour',
  'line-item/day',
  'line-item/night',
];

const toFiniteNumber = value => {
  const number = value && typeof value.toNumber === 'function' ? value.toNumber() : Number(value);

  return Number.isFinite(number) ? number : null;
};

const moneyToUnits = money => {
  const amount = toFiniteNumber(money?.amount);
  return amount === null ? null : amount / 100;
};

const lineItemTotalInUnits = lineItem => {
  const lineTotal = moneyToUnits(lineItem?.lineTotal);
  if (lineTotal !== null) return lineTotal;

  const unitPrice = moneyToUnits(lineItem?.unitPrice);
  const quantity = toFiniteNumber(lineItem?.quantity) || 1;
  return unitPrice === null ? null : unitPrice * quantity;
};

const compactObject = object =>
  Object.keys(object).reduce((result, key) => {
    const value = object[key];
    if (value !== null && value !== undefined && value !== '') {
      result[key] = value;
    }
    return result;
  }, {});

const getBaseLineItem = transaction =>
  transaction?.attributes?.lineItems?.find(
    lineItem => BASE_LINE_ITEM_CODES.includes(lineItem.code) && !lineItem.reversal
  ) || null;

const getLineItemByCode = (transaction, code) =>
  transaction?.attributes?.lineItems?.find(
    lineItem => lineItem.code === code && !lineItem.reversal
  ) || null;

const getCurrency = ({ listing, transaction, baseLineItem }) =>
  baseLineItem?.unitPrice?.currency ||
  transaction?.attributes?.payinTotal?.currency ||
  listing?.attributes?.price?.currency ||
  null;

export const buildGoogleAnalyticsItem = ({ listing, transaction, orderData = {} }) => {
  const publicData = listing?.attributes?.publicData || {};
  const baseLineItem = getBaseLineItem(transaction);
  const quantity =
    toFiniteNumber(baseLineItem?.quantity) || toFiniteNumber(orderData?.quantity) || 1;
  const unitPrice =
    moneyToUnits(baseLineItem?.unitPrice) ?? moneyToUnits(listing?.attributes?.price);

  return compactObject({
    item_id: listing?.id?.uuid,
    item_name: listing?.attributes?.title,
    affiliation: 'BowTackle',
    item_brand: publicData.brand || publicData.manufacturer,
    item_category: publicData.category,
    item_category2: publicData.subcategory,
    price: unitPrice,
    quantity,
  });
};

export const buildCheckoutEventParams = ({ listing, transaction, orderData = {} }) => {
  const baseLineItem = getBaseLineItem(transaction);
  const currency = getCurrency({ listing, transaction, baseLineItem });
  const value = lineItemTotalInUnits(baseLineItem);

  if (!listing?.id?.uuid || !currency || value === null) return null;

  return {
    currency,
    value,
    items: [buildGoogleAnalyticsItem({ listing, transaction, orderData })],
  };
};

export const buildPurchaseEventParams = ({ listing, transaction, orderData = {} }) => {
  const checkoutParams = buildCheckoutEventParams({ listing, transaction, orderData });
  const transactionId = transaction?.id?.uuid;

  if (!checkoutParams || !transactionId) return null;

  const shipping = lineItemTotalInUnits(getLineItemByCode(transaction, 'line-item/shipping-fee'));
  const tax = lineItemTotalInUnits(getLineItemByCode(transaction, 'line-item/tax'));

  return compactObject({
    transaction_id: transactionId,
    ...checkoutParams,
    shipping,
    tax,
  });
};

/**
 * Queue an event for the Google tag published through GTM-P4VMDX5Q.
 * `send_to` keeps these business events scoped to the confirmed GA4 destination.
 */
export const sendGoogleAnalyticsEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined') return false;

  window.dataLayer = window.dataLayer || [];
  const params = { ...eventParams, send_to: GOOGLE_ANALYTICS_DESTINATION_ID };

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  } else {
    // Match the queue format used by the official gtag snippet while GTM loads asynchronously.
    const enqueueGtagCommand = function() {
      window.dataLayer.push(arguments);
    };
    enqueueGtagCommand('event', eventName, params);
  }

  return true;
};

export const trackSignUp = ({ method, userType }) =>
  sendGoogleAnalyticsEvent('sign_up', compactObject({ method, user_type: userType }));

export const trackBeginCheckout = params => {
  const eventParams = buildCheckoutEventParams(params);
  return eventParams ? sendGoogleAnalyticsEvent('begin_checkout', eventParams) : false;
};

export const trackPurchase = params => {
  const eventParams = buildPurchaseEventParams(params);
  return eventParams ? sendGoogleAnalyticsEvent('purchase', eventParams) : false;
};

export { GOOGLE_ANALYTICS_DESTINATION_ID };
